<template>
  <OrganisationsTable
    :rows="organisations"
    :loading="isLoading || isDeletingOrganisation"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :key="actionsCompleted"
    :has-more-items="hasNextPage"
    :removable="true"
    :editable="true"
    @load="fetchNextPage"
    @search="search($event)"
    @create="showCreateOrganisationModal = true"
    @remove="
      (row) => {
        activeOrganisation = row;
        showDeleteOrganisationModal = true;
      }
    "
    @edit="
      (row) => {
        activeOrganisation = row;
        showUpdateOrganisationModal = true;
      }
    "
    @click-row="onClickOrganisationRow"
  />

  <!-- CREATE -->
  <MutationOrganisationModal
    v-if="showCreateOrganisationModal"
    @close="showCreateOrganisationModal = false"
    @mutate="onCreateOrganisation"
    :loading="isCreatingOrganisation"
  />

  <!-- UPDATE -->
  <MutationOrganisationModal
    v-if="showUpdateOrganisationModal && activeOrganisation"
    :organisation="activeOrganisation"
    :loading="isUpdatingOrganisation"
    @close="closeUpdateModal"
    @mutate="onUpdateOrganisation"
  />

  <InfuseConfirmModal
    v-if="showDeleteOrganisationModal && activeOrganisation"
    :action-title="t('iam.organisations.header.delete_organisation')"
    :action-description="
      t('iam.organisations.info.delete_organisation', { organisation: activeOrganisation?.name || '' })
    "
    verification-text="delete"
    @confirm="onDeleteOrganisation"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { CreateOrganisation, Organisation, UpdateOrganisation } from '@infuse/api/iam/organisation';
import {
  useCreateTenantOrganisation,
  useDeleteTenantOrganisation,
  useUpdateTenantOrganisation
} from '@infuse/iam/tenant/mutations';
import { useTenantOrganisations } from '@infuse/iam/tenant/queries';
import { MutationOrganisationModal, OrganisationsTable } from '@infuse/iam/organisation/ui';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { Tenant, tenantOrganisationPermissions } from '@infuse/api/iam/tenant';

const { t } = useI18n();

const props = defineProps<{
  model?: Tenant;
}>();

defineEmits(['reloadModel']);

const activeOrganisation = ref<Organisation | undefined>(undefined);

const {
  organisations,
  fetchParentId: fetchTenantId,
  currentPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
  searchQuery
} = useTenantOrganisations();
const { mutateAsync: createOrganisation, isPending: isCreatingOrganisation } = useCreateTenantOrganisation();
const { mutateAsync: updateOrganisation, isPending: isUpdatingOrganisation } = useUpdateTenantOrganisation();
const { mutateAsync: deleteOrganisation, isPending: isDeletingOrganisation } = useDeleteTenantOrganisation();

const actionsCompleted = ref(0);
const showCreateOrganisationModal = ref(false);
const showUpdateOrganisationModal = ref(false);
const showDeleteOrganisationModal = ref(false);

const router = useRouter();

watch(
  () => props.model,
  (tenant) => {
    if (!tenant) {
      return;
    }

    fetchTenantId.value = tenant.id;
  },
  { immediate: true }
);

watch(
  () => searchQuery.value,
  (search) => {
    searchQuery.value = search;
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
}

async function onCreateOrganisation(vars: { organisation: CreateOrganisation }): Promise<void> {
  if (!props.model) {
    return;
  }
  await createOrganisation({ parentId: props.model.id, organisation: vars.organisation });
  showCreateOrganisationModal.value = false;
}

async function onDeleteOrganisation(): Promise<void> {
  if (!activeOrganisation.value || !props.model) {
    return;
  }
  await deleteOrganisation({ parentId: props.model.id, id: activeOrganisation.value.id });
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteOrganisationModal.value = false;
  activeOrganisation.value = undefined;
}

async function onUpdateOrganisation(vars: { id: string; organisation: UpdateOrganisation }): Promise<void> {
  if (!props.model) {
    return;
  }
  await updateOrganisation({ parentId: props.model.id, id: vars.id, organisation: vars.organisation });
  closeUpdateModal();
}

function closeUpdateModal() {
  showUpdateOrganisationModal.value = false;
  activeOrganisation.value = undefined;
}

function onClickOrganisationRow(row: Organisation) {
  router.push({ name: 'organisation.details', params: { id: row.id } });
}
</script>
